import React, { useState, useEffect } from 'react';
import ContentCard from '../../AiAssistantChat/RichContext/ContentCard';
import {
  isSleepSummaryLike,
  normalizeRichContentType,
} from '../../AiAssistantChat/RichContext/contentCardUtils';
import {
  getMealSummaryPayloadFromSignals,
  isMealSignal,
} from '../../AiAssistantChat/RichContext/mealSignalUtils';
import type { UnifiedChatRichContext } from '../../../data-access';
import './RichContextSummary.scss';
import { useTranslation } from 'react-i18next';

type Props = {
  sessionType: 'sleep' | 'meal' | 'workout';
  richContext?: UnifiedChatRichContext | null;
  onEditMealContent?: () => void;
  enableMealSuggestionAccordion?: boolean;
};

const toContentList = (
  richContext: UnifiedChatRichContext | null | undefined
): Array<{ type: string; payload: Record<string, unknown> }> => {
  if (!richContext) return [];

  const rawContent = richContext.content;
  const contentList = rawContent ? (Array.isArray(rawContent) ? rawContent : [rawContent]) : [];

  return contentList.flatMap((content) => {
    if (!content || typeof content !== 'object') return [];
    const type = typeof (content as any).type === 'string' ? (content as any).type.trim() : '';
    const payload =
      (content as any).payload &&
      typeof (content as any).payload === 'object' &&
      !Array.isArray((content as any).payload)
        ? ((content as any).payload as Record<string, unknown>)
        : null;
    if (!type || !payload) return [];
    return [{ type, payload }];
  });
};

const getMealSummaryPayload = (
  richContext: UnifiedChatRichContext | null | undefined
): Record<string, unknown> | null => {
  if (!richContext) return null;

  const contentList = toContentList(richContext);
  for (const content of contentList) {
    const normalizedType = normalizeRichContentType(content.type);
    if (
      normalizedType === 'meal_suggestion' &&
      content.payload.meal_content &&
      typeof content.payload.meal_content === 'object' &&
      !Array.isArray(content.payload.meal_content)
    ) {
      return content.payload.meal_content as Record<string, unknown>;
    }
  }

  const mealPayload = getMealSummaryPayloadFromSignals(richContext);
  if (mealPayload) return mealPayload;

  const signalMealDetails = Array.isArray(richContext.signals)
    ? richContext.signals.flatMap((signal) => {
        if (
          !isMealSignal(signal) ||
          !signal?.data ||
          typeof signal.data !== 'object' ||
          Array.isArray(signal.data)
        )
          return [];
        const mealDetails = (signal.data as Record<string, unknown>).meal_details;
        return Array.isArray(mealDetails) ? mealDetails : [];
      })
    : [];

  if (signalMealDetails.length > 0) {
    return { meal_details: signalMealDetails };
  }

  return null;
};

const getSleepSummaryPayload = (
  richContext: UnifiedChatRichContext | null | undefined
): Record<string, unknown> | null => {
  if (!richContext) return null;

  const candidatePayloads: Array<Record<string, unknown>> = [];
  const addCandidate = (candidate: unknown) => {
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      candidatePayloads.push(candidate as Record<string, unknown>);
    }
  };

  const contentList = toContentList(richContext);
  for (const content of contentList) {
    const normalizedType = normalizeRichContentType(content.type);
    if (normalizedType === 'sleep_summary') {
      addCandidate(content.payload);
    }
    addCandidate((content.payload as any).sleep_summary);
    addCandidate((content.payload as any).sleepsummary);
    addCandidate((content.payload as any).sleep);
  }

  if (Array.isArray(richContext.signals)) {
    for (const signal of richContext.signals) {
      const data =
        signal?.data && typeof signal.data === 'object' && !Array.isArray(signal.data)
          ? (signal.data as Record<string, unknown>)
          : null;
      if (!data) continue;
      addCandidate(data);
      addCandidate(data.sleep_summary);
      addCandidate(data.sleepsummary);
      addCandidate(data.sleep);
    }
  }

  for (const candidate of candidatePayloads) {
    if (isSleepSummaryLike(candidate)) return candidate;
  }

  return null;
};

const getWorkoutPlanPayload = (
  richContext: UnifiedChatRichContext | null | undefined
): Record<string, unknown> | null => {
  if (!richContext) return null;
  const contentList = toContentList(richContext);
  for (const content of contentList) {
    if (normalizeRichContentType(content.type) === 'workout_plan') return content.payload;
  }
  return null;
};

const getWorkoutSummaryPayload = (
  richContext: UnifiedChatRichContext | null | undefined
): Record<string, unknown> | null => {
  if (!richContext) return null;
  const contentList = toContentList(richContext);
  for (const content of contentList) {
    if (normalizeRichContentType(content.type) === 'workout_summary') return content.payload;
  }
  return null;
};

const RichContextSummary: React.FC<Props> = ({
  sessionType,
  richContext,
  onEditMealContent,
  enableMealSuggestionAccordion = false,
}) => {
  const { t } = useTranslation();
  const contentList = toContentList(richContext);
  const firstMealSuggestion =
    sessionType === 'meal'
      ? (contentList.find((c) => normalizeRichContentType(c.type) === 'meal_suggestion') ?? null)
      : null;
  const mealSummaryPayload = sessionType === 'meal' ? getMealSummaryPayload(richContext) : null;
  const sleepSummaryPayload = sessionType === 'sleep' ? getSleepSummaryPayload(richContext) : null;
  const workoutSummaryPayload =
    sessionType === 'workout' ? getWorkoutSummaryPayload(richContext) : null;
  const workoutPlanPayload = sessionType === 'workout' ? getWorkoutPlanPayload(richContext) : null;

  const hasMealSummary = sessionType === 'meal' && !!mealSummaryPayload;
  const hasMealSuggestion = sessionType === 'meal' && !!firstMealSuggestion;
  const shouldRenderCollapsibleMealSuggestion =
    enableMealSuggestionAccordion && hasMealSummary && hasMealSuggestion;

  const [isMealSuggestionCollapsed, setIsMealSuggestionCollapsed] = useState(true);

  useEffect(() => {
    if (shouldRenderCollapsibleMealSuggestion) {
      setIsMealSuggestionCollapsed(true);
    }
  }, [shouldRenderCollapsibleMealSuggestion, richContext]);

  if (shouldRenderCollapsibleMealSuggestion) {
    return (
      <div className="rc-summary">
        <ContentCard
          type="meal_content"
          payload={mealSummaryPayload as Record<string, unknown>}
          enrichmentPending={Boolean(richContext?.enrichment_pending)}
          enrichmentStatusText={richContext?.status_text}
          onEditMealContent={onEditMealContent}
        />
        <div className="rc-summary__accordion">
          <button
            type="button"
            className="rc-summary__accordion-header"
            onClick={() => setIsMealSuggestionCollapsed((prev) => !prev)}
            aria-expanded={!isMealSuggestionCollapsed}
          >
            <span className="rc-summary__accordion-title">
              {t('calendar.suggested.mealSuggestion')}
            </span>
            <span
              className={`rc-summary__accordion-chevron${isMealSuggestionCollapsed ? '' : ' rc-summary__accordion-chevron--open'}`}
            >
              ›
            </span>
          </button>
          {!isMealSuggestionCollapsed && (
            <div className="rc-summary__accordion-body">
              <ContentCard
                type="meal_suggestion"
                payload={firstMealSuggestion?.payload as Record<string, unknown>}
                enrichmentPending={Boolean(richContext?.enrichment_pending)}
                enrichmentStatusText={richContext?.status_text}
                hideHeader
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  const contentToRender =
    (sessionType === 'meal' && firstMealSuggestion
      ? { type: 'meal_suggestion', payload: firstMealSuggestion.payload }
      : null) ||
    (sessionType === 'meal' && mealSummaryPayload
      ? { type: 'meal_content', payload: mealSummaryPayload }
      : null) ||
    (sessionType === 'sleep' && sleepSummaryPayload
      ? { type: 'sleep_summary', payload: sleepSummaryPayload }
      : null) ||
    (sessionType === 'workout' && workoutSummaryPayload
      ? { type: 'workout_summary', payload: workoutSummaryPayload }
      : null) ||
    (sessionType === 'workout' && workoutPlanPayload
      ? { type: 'workout_plan', payload: workoutPlanPayload }
      : null) ||
    contentList[0] ||
    null;

  if (!contentToRender) return null;

  return (
    <div className="rc-summary">
      <ContentCard
        type={contentToRender.type}
        payload={contentToRender.payload}
        enrichmentPending={Boolean(richContext?.enrichment_pending)}
        enrichmentStatusText={richContext?.status_text}
        onEditMealContent={sessionType === 'meal' ? onEditMealContent : undefined}
      />
    </div>
  );
};

export default RichContextSummary;
